import React from "react";
import {AnswerStatus, MCColors, MCHeightLg, MCHeightSm} from "./questionConstants";
import Icon, {IconName} from "../../common/Icon";
import MultipleChoiceLabel from "./MultipleChoiceLabel";
import {ParagraphLineHeight} from "../prompts/TextPrompt";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import {InputType, MCQuestionDefinition, QuestionDefinition} from "../../../state/questions";
import {AggregateResponses, Letters, TriviaStatus} from "../../../state/state";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import UserProvidedText from "engage-client/components/UserProvidedText";
import {getAnswerDistribution, getAnswerPercentages} from "../display/MultipleChoiceDisplay";

type StyleProps = {
	answerBG:string
	poll: boolean
}

const SpaceBetween = 20;
const TopSpace = 24;

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	explanationContainer: {
		backgroundColor: theme.questionExplanation.backgroundColor,
		borderRadius: 10,
		position: "relative",
		[theme.breakpoints.up("lg")] : {
			//Force the explanation and sources below the answer box which is
			// absolutely positioned to facilitate animation.
			padding: `${TopSpace + SpaceBetween + MCHeightLg}px 80px 40px 80px`,
			margin: [0,40,0,0],
		},
		[theme.breakpoints.down("md")] : {
			padding: `${TopSpace + SpaceBetween + MCHeightSm}px 40px 40px 40px`,
			margin: [0,15],
		},
		[theme.breakpoints.down("xs")] : {
			maxWidth: "90vw"
		},
	},
	answer: {
		backgroundColor: props => props.answerBG,
		borderRadius: 5,
		boxShadow: "0px 3px 6px #00000029",
		[theme.breakpoints.down("md")] : {
			height: MCHeightSm,
			left: 30,
			right: 30,
			padding: "2px 14px",
		},
		[theme.breakpoints.up("lg")] : {
			height: MCHeightLg,
			left: 70,
			right: 70,
			padding: "2px 28px",
		},
		display: "flex",
		flexDirection: props => props.poll ? "column" : "row",
		alignItems: props => props.poll ? "left" : "center",
		color: "white",
		position: "absolute",
		top: TopSpace,
	},
	answerText: {
		margin: "0 17px",
		[theme.breakpoints.down("md")] : {
			margin: "0 13px",
		},
		flex: 1,
		display: "flex",
		alignItems: "center",
	},
	explanation: {
		marginBottom: SpaceBetween,
		minHeight: 125,
		maxHeight: 250
	},
	sources: {
		marginBottom: SpaceBetween,
		minHeight: 50,
		wordWrap: "break-word",
	},
	checkMark: {
		[theme.breakpoints.down("md")] : {
			width: 19,
			height: 19
		},
	},
	poll: {
		margin: 2
	}
}));

type ExplanationProps = {
	question: QuestionDefinition
	aggregateResponses?: AggregateResponses
	showFastest: boolean
}

export default function Explanation({question, aggregateResponses, showFastest} : ExplanationProps) {
	const theme = useTheme<Theme>();
	
	let props:StyleProps = {
		answerBG: "none",
		poll: question?.poll ?? false
	};

	let answer = "";
	let answerDistribution;
	let answerPercentage;
	const pollArray = [];
	if(question.inputType === InputType.MC) {
		if(question.poll) {
			answerDistribution = getAnswerDistribution(TriviaStatus.QuestionSummary, aggregateResponses, question as MCQuestionDefinition) || new Array(question.options.length).fill(0);
			answerPercentage = getAnswerPercentages(TriviaStatus.QuestionSummary, answerDistribution, question as MCQuestionDefinition);
			for (let i = 0; i < question.options.length; i++) {
				let answerObj = {
					letter: Letters[i],
					response: question.options[i],
					number: answerDistribution[i],
					percent: answerPercentage[i]
				}; 
				pollArray.push(answerObj);
			}
		}
		answer = question.options[question.correctIndex!];
		props.answerBG = MCColors(theme)[question.correctIndex!];
	} else if(question.inputType === InputType.Sort) {
		//TODO
	} else if(question.inputType === InputType.Number) {
		answer = `${question.correctNumber}`;
	} else if(question.inputType === InputType.Phrase) {
		let count = aggregateResponses?.optionCounts[1] ?? 0;
		answer = `${count} player${count !== 1 ? "s" : ""} got it right!`;
	}
	const classes = useStyles(props);

	let decorator = <Icon className={classes.checkMark} name={IconName.whiteCheck}/>;
	if(question.inputType === InputType.MC) {
		decorator = <MultipleChoiceLabel color={props.answerBG} status={AnswerStatus.correct}>
			{Letters[question.correctIndex!]}
		</MultipleChoiceLabel>;
	}
	
	const pollResults = pollArray.sort((a, b) => b.number - a.number).map((el) => 
		<UserProvidedText maxFont={[18,12]} key={el.letter} className={classes.poll}>
			{el.letter}: {el.response} - {el.number} votes ({el.percent.toFixed(1)}%)
		</UserProvidedText>);
	

	return <div className={classes.explanationContainer}>
		<div className={classes.answer}>
			{question.poll ? pollResults : 
				<>{decorator}
				<UserProvidedText maxFont={[22,18]} className={classes.answerText}>{answer}</UserProvidedText>
				</>}
		</div>
		{question.explanation &&
			<UserProvidedText className={classes.explanation} maxFont={[22,18]}>{question.explanation}</UserProvidedText>
		}
		{question.sources &&
			<UserProvidedText className={classes.sources} breakWords={true} maxFont={[16,14]}>{question.sources}</UserProvidedText>
		}
		{aggregateResponses?.fastestResponse && showFastest &&
			<DisplayText className={classes.sources} variant={TextVariant.s16reg}
						 color="white" lineHeight={ParagraphLineHeight}>
				{`${aggregateResponses?.fastestResponse.nickname} was the fastest at ${Math.round(aggregateResponses?.fastestResponse.timeElapsed/100)/10} seconds!`}
			</DisplayText>
		}
	</div>;
}